import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { downloadBlob } from '../lib/export';

const ML = 56, MB = 34, MT = 12, MR = 14, PLOT_W = 520;

/** Draw a slice raster (RGBA nu x nv, row 0 = v0) with axes on a 2D canvas. */
export function drawSliceImage(canvas, slice, pixels) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const uLen = slice.uRange[1] - slice.uRange[0] || 1, vLen = slice.vRange[1] - slice.vRange[0] || 1;
  const plotH = Math.max(120, Math.min(400, Math.round((PLOT_W * vLen) / uLen)));
  canvas.width = ML + PLOT_W + MR;
  canvas.height = MT + plotH + MB;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const off = document.createElement('canvas');
  off.width = slice.nu; off.height = slice.nv;
  const octx = off.getContext('2d');
  if (octx) {
    octx.putImageData(new ImageData(new Uint8ClampedArray(pixels), slice.nu, slice.nv), 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.save();
    ctx.translate(ML, MT);
    if (!slice.vDown) { ctx.translate(0, plotH); ctx.scale(1, -1); } // plan view: y increases upwards
    ctx.drawImage(off, 0, 0, PLOT_W, plotH);
    ctx.restore();
  }
  ctx.strokeStyle = '#333'; ctx.fillStyle = '#222'; ctx.lineWidth = 1; ctx.font = '11px sans-serif';
  ctx.strokeRect(ML + 0.5, MT + 0.5, PLOT_W, plotH);
  const label = (v) => String(Number(v.toFixed(1)));
  for (let k = 0; k <= 5; k++) {
    const f = k / 5;
    const x = ML + f * PLOT_W;
    ctx.beginPath(); ctx.moveTo(x, MT + plotH); ctx.lineTo(x, MT + plotH + 4); ctx.stroke();
    ctx.textAlign = 'center'; ctx.fillText(label(slice.uRange[0] + f * uLen), x, MT + plotH + 16);
    const y = MT + (slice.vDown ? f : 1 - f) * plotH;
    ctx.beginPath(); ctx.moveTo(ML - 4, y); ctx.lineTo(ML, y); ctx.stroke();
    ctx.textAlign = 'right'; ctx.fillText(label(slice.vRange[0] + f * vLen), ML - 7, y + 4);
  }
  ctx.textAlign = 'center';
  ctx.fillText(slice.uLabel, ML + PLOT_W / 2, MT + plotH + 30);
  ctx.save(); ctx.translate(12, MT + plotH / 2); ctx.rotate(-Math.PI / 2); ctx.fillText(slice.vLabel, 0, 0); ctx.restore();
}

/** A single slice as a 2D image (plan or section) with axes, plus a PNG download. */
export default function SliceImageModal({ slice, pixels, title, onClose }) {
  const { C } = useTheme();
  const ref = useRef(null);
  useEffect(() => { if (ref.current) drawSliceImage(ref.current, slice, pixels); }, [slice, pixels]);
  const download = () => {
    ref.current?.toBlob((b) => { if (b) downloadBlob(b, `${(title || 'slice').replace(/[^\w.-]+/g, '_')}.png`); }, 'image/png');
  };
  return (
    <div data-testid="slice-image-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 130, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, maxWidth: '94vw', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="flex items-center gap-2 mb-2" style={{ color: C.text0 }}>
          <span className="text-sm font-semibold flex-1">{title}</span>
          <button type="button" onClick={download} className="px-2 py-1 rounded text-[11px] cursor-pointer border"
            style={{ background: C.bg3, color: C.text1, borderColor: C.border }}>Download PNG</button>
          <button type="button" onClick={onClose} className="px-2 py-1 rounded text-[11px] cursor-pointer border"
            style={{ background: C.bg3, color: C.text1, borderColor: C.border }}>Close</button>
        </div>
        <canvas ref={ref} style={{ maxWidth: '100%' }} />
      </div>
    </div>
  );
}
